import type { AluminiumComposition, EvidenceDetail, PETComposition } from 'api';
import { AIModelChoices } from 'api';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import {
  EvidenceSessionPhotoGallery,
  type EvidenceSessionPhotoGalleryItem,
  Grid,
  MediaDialog,
  Stack,
  Typography,
  useBreakpoint,
} from 'ui';

import { AiFeedbackVisibilityProvider, AiFilters, AnnotatedMediaItem, useAnnotations } from '../../../annotations';
import { MaterialAiBreakdown } from '../../../breakdowns';
import { isPetBaledAiModel } from '../../types';
import { EvidenceSessionStats } from '../EvidenceSessionStats/EvidenceSessionStats';

export const EvidenceAiDetails = ({
  photoEvidence,
  evidenceList,
  stats,
  model,
  aluminiumComposition,
  petComposition,
}: {
  photoEvidence: EvidenceSessionPhotoGalleryItem[];
  evidenceList: readonly EvidenceDetail[];
  stats: {
    failed: number;
    passed: number;
    total: number;
  };
  model: AIModelChoices;
  aluminiumComposition?: AluminiumComposition | null;
  petComposition?: PETComposition | null;
}) => {
  const { t } = useTranslation('features');
  const { isMobile } = useBreakpoint();
  const {
    selectedAnnotation,
    handleImageChange,
    hiddenCategories,
    handleHideCategory,
    handleToggleLegend,
    showLegend,
  } = useAnnotations({ model });

  const carouselMedia = photoEvidence.map((file) => file.fullResolutionSrc);

  const [selectedEvidence, setSelectedEvidence] = useState<EvidenceDetail | null>(null);

  const handleSelect = (src: string) => {
    const relatedEvidence = evidenceList.find((item) => item.file_url === src || item.preview_url === src);
    setSelectedEvidence(relatedEvidence || null);
    handleImageChange([...(relatedEvidence?.annotations || [])]);
  };

  return (
    <>
      <Grid container spacing={3} data-testid="evidence-session-details">
        <Grid
          data-testid="photo-evidence"
          size={{
            md: 6,
            xs: 12,
          }}
        >
          <Stack
            sx={{
              gap: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {t('backlog.evidence.evidenceAiDetails.aiImagesTitle')}
            </Typography>
            {photoEvidence.length ? (
              <>
                <EvidenceSessionStats {...stats} />
                <EvidenceSessionPhotoGallery
                  photoEvidence={photoEvidence}
                  maxImages={isMobile ? 6 : 8}
                  handleClick={(photo) => handleSelect(photo.src)}
                />
              </>
            ) : (
              <Typography>{t('backlog.common.noData')}</Typography>
            )}
          </Stack>
        </Grid>
        <Grid
          data-testid="composition-report"
          size={{
            md: 6,
            xs: 12,
          }}
        >
          <Typography variant="h6" gutterBottom fontWeight="bold">
            {t('backlog.evidence.evidenceAiDetails.aiAnalysisTitle')}
          </Typography>
          {(model === AIModelChoices.alu || model === AIModelChoices.briquette) && aluminiumComposition ? (
            <MaterialAiBreakdown variant={model} aluminiumComposition={aluminiumComposition} />
          ) : isPetBaledAiModel(model) && petComposition ? (
            <MaterialAiBreakdown variant={model} petComposition={petComposition} />
          ) : (
            <Typography>{t('backlog.common.noData')}</Typography>
          )}
        </Grid>
      </Grid>
      <AiFeedbackVisibilityProvider>
        <MediaDialog
          open={Boolean(selectedEvidence?.file_url)}
          selectedMedium={selectedEvidence?.file_url ?? ''}
          onClose={() => setSelectedEvidence(null)}
          media={carouselMedia}
          handleNavigate={handleSelect}
          imageCount={carouselMedia.length}
          slotProps={{
            imageCarousel: {
              slotProps: {
                ...(selectedAnnotation && selectedEvidence && model
                  ? {
                      mediaItemSlot: (
                        <AnnotatedMediaItem
                          image={selectedEvidence.file_url ?? ''}
                          annotations={selectedAnnotation}
                          hiddenCategories={hiddenCategories}
                          showLegend={showLegend}
                          model={model}
                          showAerosols={false}
                          evidenceId={selectedEvidence.id}
                        />
                      ),
                      sidebar: {
                        children: (
                          <AiFilters
                            selectedAnnotation={selectedAnnotation}
                            hiddenCategories={hiddenCategories}
                            handleHideCategory={handleHideCategory}
                            handleToggleLegend={handleToggleLegend}
                            showLegend={showLegend}
                            model={model}
                            showAerosols={false}
                          />
                        ),
                      },
                    }
                  : null),
              },
            },
          }}
        />
      </AiFeedbackVisibilityProvider>
    </>
  );
};
